import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws/call-signaling';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const CallContext = createContext(null);

export const useCall = () => useContext(CallContext);

export const CallProvider = ({ children, isAuthenticated }) => {
  const [callState, setCallState] = useState('idle'); // idle | outgoing | incoming | connected
  const [callInfo, setCallInfo] = useState(null); // { callId, callType, otherUser }
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const reconnectTimer = useRef(null);
  const durationTimer = useRef(null);
  const pendingCandidates = useRef([]);
  const callStateRef = useRef('idle');
  const handleMessageRef = useRef(null);

  // Keep ref in sync
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  const sendSignal = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const cleanupCall = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStream) {
      localStream.getTracks().forEach(t => t.stop());
    }
    setLocalStream(null);
    setRemoteStream(null);
    setCallState('idle');
    setCallInfo(null);
    setIsMuted(false);
    setIsVideoOff(false);
    setCallDuration(0);
    pendingCandidates.current = [];
    if (durationTimer.current) {
      clearInterval(durationTimer.current);
      durationTimer.current = null;
    }
  }, [localStream]);

  const createPeerConnection = useCallback((targetId) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignal({
          type: 'ice_candidate',
          target_id: targetId,
          candidate: event.candidate.toJSON(),
        });
      }
    };

    pc.ontrack = (event) => {
      const [stream] = event.streams;
      setRemoteStream(stream);
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        if (callStateRef.current === 'connected') {
          toast.error('Call connection lost');
          cleanupCall();
        }
      }
    };

    pcRef.current = pc;
    return pc;
  }, [sendSignal, cleanupCall]);

  const startMedia = useCallback(async (callType) => {
    try {
      const constraints = {
        audio: true,
        video: callType === 'video' ? { width: 640, height: 480, facingMode: 'user' } : false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);
      return stream;
    } catch (err) {
      toast.error('Could not access camera/microphone. Please check permissions.');
      return null;
    }
  }, []);

  // --- Public Actions ---

  const initiateCall = useCallback(async (receiverId, receiverName, receiverAvatar, receiverUsername, callType = 'voice') => {
    if (callStateRef.current !== 'idle') {
      toast.error('You are already in a call');
      return;
    }

    setCallState('outgoing');
    setCallInfo({
      callId: null,
      callType,
      otherUser: {
        id: receiverId,
        full_name: receiverName,
        avatar_url: receiverAvatar,
        username: receiverUsername,
      },
    });

    sendSignal({
      type: 'call_initiate',
      receiver_id: receiverId,
      call_type: callType,
    });
  }, [sendSignal]);

  const declineCall = useCallback(() => {
    if (callInfo?.callId) {
      sendSignal({ type: 'call_decline', call_id: callInfo.callId });
    }
    cleanupCall();
  }, [callInfo, sendSignal, cleanupCall]);

  const acceptCall = useCallback(async () => {
    if (!callInfo?.callId) return;

    const stream = await startMedia(callInfo.callType);
    if (!stream) {
      declineCall();
      return;
    }

    const pc = createPeerConnection(callInfo.otherUser.id);
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    // Process any ICE candidates that arrived before we created the PC
    for (const candidate of pendingCandidates.current) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) { /* ignore */ }
    }
    pendingCandidates.current = [];

    sendSignal({ type: 'call_accept', call_id: callInfo.callId });
    setCallState('connected');

    // Start duration timer
    durationTimer.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callInfo, startMedia, createPeerConnection, sendSignal, declineCall]);

  const endCall = useCallback(() => {
    if (callInfo?.callId) {
      sendSignal({ type: 'call_end', call_id: callInfo.callId });
    }
    cleanupCall();
  }, [callInfo, sendSignal, cleanupCall]);

  const cancelCall = useCallback(() => {
    if (callInfo?.callId) {
      sendSignal({ type: 'call_cancel', call_id: callInfo.callId });
    }
    cleanupCall();
  }, [callInfo, sendSignal, cleanupCall]);

  const toggleMute = useCallback(() => {
    if (localStream) {
      localStream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(prev => !prev);
    }
  }, [localStream]);

  const toggleVideo = useCallback(() => {
    if (localStream) {
      localStream.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsVideoOff(prev => !prev);
    }
  }, [localStream]);

  // --- WebSocket Message Handler ---

  const handleMessage = useCallback(async (data) => {
    switch (data.type) {
      case 'incoming_call': {
        if (callStateRef.current !== 'idle') {
          // Auto-decline if already in a call
          sendSignal({ type: 'call_decline', call_id: data.call_id });
          return;
        }
        setCallState('incoming');
        setCallInfo({
          callId: data.call_id,
          callType: data.call_type,
          otherUser: {
            id: data.caller_id,
            full_name: data.caller_name,
            avatar_url: data.caller_avatar,
            username: data.caller_username,
          },
        });
        break;
      }

      case 'call_ringing': {
        setCallInfo(prev => prev ? { ...prev, callId: data.call_id } : prev);
        break;
      }

      case 'call_accepted': {
        // Caller side: start media and create offer
        const stream = await startMedia(callInfo?.callType || 'voice');
        if (!stream) {
          sendSignal({ type: 'call_end', call_id: data.call_id });
          cleanupCall();
          return;
        }

        const otherId = callInfo?.otherUser?.id;
        if (!otherId) return;

        const pc = createPeerConnection(otherId);
        stream.getTracks().forEach(track => pc.addTrack(track, stream));

        // Process pending ICE candidates
        for (const candidate of pendingCandidates.current) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) { /* ignore */ }
        }
        pendingCandidates.current = [];

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        sendSignal({
          type: 'sdp_offer',
          target_id: otherId,
          sdp: offer,
        });

        setCallState('connected');
        durationTimer.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
        break;
      }

      case 'sdp_offer': {
        if (!pcRef.current) return;
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
        const answer = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answer);

        sendSignal({
          type: 'sdp_answer',
          target_id: data.sender_id,
          sdp: answer,
        });
        break;
      }

      case 'sdp_answer': {
        if (!pcRef.current) return;
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
        break;
      }

      case 'ice_candidate': {
        if (pcRef.current && pcRef.current.remoteDescription) {
          try {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          } catch (e) { /* ignore */ }
        } else {
          pendingCandidates.current.push(data.candidate);
        }
        break;
      }

      case 'call_declined': {
        toast('Call was declined');
        cleanupCall();
        break;
      }

      case 'call_ended': {
        toast('Call ended');
        cleanupCall();
        break;
      }

      case 'call_cancelled': {
        toast('Call was cancelled');
        cleanupCall();
        break;
      }

      case 'call_busy': {
        toast('User is busy on another call');
        cleanupCall();
        break;
      }

      case 'call_error': {
        toast.error(data.message || 'Call failed');
        cleanupCall();
        break;
      }

      default:
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callInfo, startMedia, createPeerConnection, sendSignal, cleanupCall]);

  // Keep message handler ref in sync
  useEffect(() => { handleMessageRef.current = handleMessage; }, [handleMessage]);

  // --- WebSocket Connection ---

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;

    // Token sent via cookie; pass in URL as fallback for WS upgrade
    const token = sessionStorage.getItem('token') || '';
    const ws = new WebSocket(token ? `${WS_URL}?token=${token}` : WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.type !== 'pong') {
          handleMessageRef.current?.(data);
        }
      } catch (e) { /* ignore */ }
    };

    ws.onclose = () => {
      setWsConnected(false);
      wsRef.current = null;
      if (!reconnectTimer.current) {
        reconnectTimer.current = setTimeout(() => {
          reconnectTimer.current = null;
          connectWs();
        }, 5000);
      }
    };

    ws.onerror = () => { ws.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const disconnectWs = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
    setWsConnected(false);
  }, []);

  // Auto-connect when authenticated, disconnect when not
  useEffect(() => {
    if (isAuthenticated) {
      connectWs();
    } else {
      disconnectWs();
    }
    return () => disconnectWs();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ping to keep alive
  useEffect(() => {
    const interval = setInterval(() => {
      sendSignal({ type: 'ping' });
    }, 30000);
    return () => clearInterval(interval);
  }, [sendSignal]);

  const value = {
    callState,
    callInfo,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    callDuration,
    wsConnected,
    initiateCall,
    acceptCall,
    declineCall,
    endCall,
    cancelCall,
    toggleMute,
    toggleVideo,
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
};
